import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Card, CardContent } from '../../../components/ui/Card';
import { ArrowLeft, Plus } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

export default function VATReturns() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returns, setReturns] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    returnPeriodStart: '',
    returnPeriodEnd: '',
    returnType: 'quarterly',
  });

  useEffect(() => {
    fetchReturns();
  }, []);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await reportsAPI.getVATReturns();
      setReturns(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load VAT returns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      setError('');
      await reportsAPI.createVATReturn(createForm);
      setShowCreateForm(false);
      setCreateForm({ returnPeriodStart: '', returnPeriodEnd: '', returnType: 'quarterly' });
      fetchReturns();
    } catch (err) {
      setError(err.message || 'Failed to create VAT return');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">VAT Returns</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Manage and submit VAT returns</p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button variant="primary" size="sm" onClick={() => setShowCreateForm(!showCreateForm)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Return
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        {showCreateForm && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-h2 font-medium mb-4 text-[var(--ds-text-primary)]">Create VAT Return</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FormLabel>Period Start</FormLabel>
                  <Input
                    type="date"
                    value={createForm.returnPeriodStart}
                    onChange={(e) => setCreateForm({ ...createForm, returnPeriodStart: e.target.value })}
                    className="h-8"
                  />
                </div>
                <div>
                  <FormLabel>Period End</FormLabel>
                  <Input
                    type="date"
                    value={createForm.returnPeriodEnd}
                    onChange={(e) => setCreateForm({ ...createForm, returnPeriodEnd: e.target.value })}
                    className="h-8"
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="primary" size="sm" onClick={handleCreate} className="w-full">Create</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : (
            <CardContent className="p-6">
              {returns.length === 0 ? (
                <p className="text-center text-xs text-[var(--ds-text-secondary)] py-8">No VAT returns found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow isHeader>
                      <TableHead align="left">Period</TableHead>
                      <TableHead align="right">VAT Due</TableHead>
                      <TableHead align="right">VAT Reclaimed</TableHead>
                      <TableHead align="right">Net VAT Due</TableHead>
                      <TableHead align="center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {returns.map((ret) => (
                      <TableRow key={ret.id}>
                        <TableCell align="left">
                          {formatDate(ret.returnPeriodStart)} - {formatDate(ret.returnPeriodEnd)}
                        </TableCell>
                        <TableCell align="right" className="tabular-nums">
                          {currency(ret.vatDueOnSales || 0)}
                        </TableCell>
                        <TableCell align="right" className="tabular-nums">
                          {currency(ret.vatReclaimedOnPurchases || 0)}
                        </TableCell>
                        <TableCell align="right" className="tabular-nums font-semibold">
                          {currency(ret.netVatDue || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            ret.status === 'submitted' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            ret.status === 'approved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}>
                            {ret.status || 'draft'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
